import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, X, ChevronDown, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/* ─────────────────────────────────────────────────────────────
 *  Granular GDPR consent banner.
 *  Stores `tofbox_gdpr_consent` = JSON { essential, analytics, marketing }.
 *  Legacy values "accepted" / "denied" still readable through getConsent().
 * ──────────────────────────────────────────────────────────── */

const CONSENT_KEY = 'tofbox_gdpr_consent';

/** Consent pill — extracted to a stable top-level component so React
 *  doesn't remount the entire subtree on every render of the parent
 *  banner (fixes react/no-unstable-nested-components). */
function ConsentPill({ k, label, desc, locked, prefs, onToggle, alwaysOnLabel }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(k)}
      disabled={locked}
      data-testid={`gdpr-toggle-${k}`}
      className="w-full flex items-start gap-3 text-left p-3 rounded-lg border border-[#1e1e3a] hover:border-[#534AB7]/60 transition-colors disabled:opacity-80"
    >
      <span
        className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
          prefs[k] || locked ? 'bg-[#534AB7]' : 'bg-white/10'
        }`}
      >
        {(prefs[k] || locked) && <Check size={12} className="text-white" />}
      </span>
      <span className="flex-1">
        <span className="block text-sm font-bold text-white">
          {label} {locked && <span className="text-[10px] text-[#AFA9EC] ml-1">({alwaysOnLabel})</span>}
        </span>
        <span className="block text-xs text-white/60 leading-relaxed mt-0.5">{desc}</span>
      </span>
    </button>
  );
}

export function getConsent() {
  const raw = localStorage.getItem(CONSENT_KEY);
  if (!raw) return null;
  if (raw === 'accepted') return { essential: true, analytics: true, marketing: true };
  if (raw === 'denied')   return { essential: true, analytics: false, marketing: false };
  try { return JSON.parse(raw); } catch { return null; }
}

/** Force-reopen the consent banner from anywhere in the app
 *  (e.g. "Manage cookies" button in Settings → Privacy). */
export function openConsentBanner() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('tofbox_open_consent'));
}

function persist(prefs) {
  const stamped = { ...prefs, essential: true, updated_at: new Date().toISOString() };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(stamped));
  window.dispatchEvent(new CustomEvent('tofbox_consent_changed', { detail: stamped }));
}

export default function ConsentBanner() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, analytics: false, marketing: false });

  useEffect(() => {
    if (!localStorage.getItem(CONSENT_KEY)) {
      const timer = setTimeout(() => setShow(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  // Allow other parts of the app (Settings → Privacy) to re-open the banner
  // on demand, e.g. so users can revoke their previous choice without having
  // to clear their browser storage.
  useEffect(() => {
    const reopen = () => {
      const existing = (() => {
        try { return JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null') || {}; }
        catch { return {}; }
      })();
      setPrefs({
        essential: true,
        analytics: !!existing.analytics,
        marketing: !!existing.marketing,
      });
      setExpanded(true);
      setShow(true);
    };
    window.addEventListener('tofbox_open_consent', reopen);
    return () => window.removeEventListener('tofbox_open_consent', reopen);
  }, []);

  const acceptAll = () => {
    persist({ essential: true, analytics: true, marketing: true });
    setShow(false);
  };
  const denyAll = () => {
    persist({ essential: true, analytics: false, marketing: false });
    setShow(false);
  };
  const saveCustom = () => {
    persist({ ...prefs, essential: true });
    setShow(false);
  };

  const toggle = (key) => {
    if (key === 'essential') return;
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  const alwaysOn = t('consent.always');

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="gdpr-banner"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
          className="fixed bottom-24 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-[60] bg-[#0f0f1a] border border-[#534AB7]/50 rounded-xl shadow-2xl overflow-hidden"
        >
          <div className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <Shield size={18} className="text-[#AFA9EC] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-bold text-white mb-1">{t('consent.title')}</div>
                <p className="text-xs text-white/70 leading-relaxed">
                  {t('consent.body')}
                </p>
              </div>
              <button onClick={denyAll} aria-label={t('consent.closeAria')} data-testid="gdpr-close-btn">
                <X size={16} className="text-white/40 hover:text-white" />
              </button>
            </div>

            <AnimatePresence initial={false}>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-2 mb-3">
                    <ConsentPill
                      k="essential"
                      label={t('consent.essentialLabel')}
                      desc={t('consent.essentialDesc')}
                      locked
                      prefs={prefs}
                      onToggle={toggle}
                      alwaysOnLabel={alwaysOn}
                    />
                    <ConsentPill
                      k="analytics"
                      label={t('consent.analyticsLabel')}
                      desc={t('consent.analyticsDesc')}
                      prefs={prefs}
                      onToggle={toggle}
                      alwaysOnLabel={alwaysOn}
                    />
                    <ConsentPill
                      k="marketing"
                      label={t('consent.marketingLabel')}
                      desc={t('consent.marketingDesc')}
                      prefs={prefs}
                      onToggle={toggle}
                      alwaysOnLabel={alwaysOn}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="text-[11px] uppercase tracking-widest text-[#AFA9EC] hover:text-white flex items-center gap-1 mb-3 font-bold"
              data-testid="gdpr-customize-toggle"
            >
              {expanded ? t('consent.hideDetails') : t('consent.customize')}
              <ChevronDown size={12} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>

            <a
              href="/legal#cookies"
              data-testid="gdpr-policy-link"
              className="block text-[10px] text-white/40 hover:text-white underline mb-3"
            >
              {t('consent.learnMore')}
            </a>

            <div className="flex gap-2">
              {expanded ? (
                <button
                  data-testid="gdpr-save-btn"
                  onClick={saveCustom}
                  className="flex-1 py-2.5 rounded-lg bg-[#534AB7] hover:bg-[#6259c7] text-white text-xs font-black uppercase tracking-widest transition-colors"
                >
                  {t('consent.save')}
                </button>
              ) : (
                <>
                  <button
                    data-testid="gdpr-decline-btn"
                    onClick={denyAll}
                    className="flex-1 py-2.5 rounded-lg border border-white/20 text-white/80 text-xs font-black uppercase tracking-widest hover:border-white/40 transition-colors"
                  >
                    {t('consent.declineAll')}
                  </button>
                  <button
                    data-testid="gdpr-accept-btn"
                    onClick={acceptAll}
                    className="flex-1 py-2.5 rounded-lg bg-[#534AB7] hover:bg-[#6259c7] text-white text-xs font-black uppercase tracking-widest transition-colors"
                  >
                    {t('consent.acceptAll')}
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
